import React, { useState, useEffect, useRef } from 'react';
import API_CONFIG from '../../apiConfig';
import CustomSelect from '../ui/CustomSelect';

interface PoseSelectionProps {
  selectedPoseId: string | null;
  setSelectedPoseId: React.Dispatch<React.SetStateAction<string | null>>;
  onPoseSelect?: (poseUrl: string, poseId: string) => void;
  disabled?: boolean;
  genderFilter: string;
  sizeFilter?: string;
  onSizeFilterChange?: (size: string) => void;
}

interface Pose {
  poseId: string;
  imageUrl: string[];
  prompt: string;
  type: string;
  timestamp: string;
  gender: string;
  generationType: string;
  poseName: string;
  liveStatus: boolean;
  size?: string;
}

const PoseSelection: React.FC<PoseSelectionProps> = ({ 
  selectedPoseId, 
  setSelectedPoseId, 
  onPoseSelect,
  disabled = false,
  genderFilter,
  sizeFilter = 'normal',
  onSizeFilterChange
}) => {
  const [poses, setPoses] = useState<Pose[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('standing');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchPoses = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_CONFIG.GET_POSE_PRESETS_URL);

        // Check HTTP status
        if (!response.ok) {
          setError('Something went wrong. Please try again.');
          return;
        }

        const data = await response.json();
        
        // Check if poses were returned
        if (data.success && data.poses && data.poses.length > 0) {
          // Filter poses to only show those with liveStatus: true
          const livePoses = data.poses.filter((pose: Pose) => pose.liveStatus === true);
          
          // Sort poses by poseId for consistent order
          const sortedPoses = livePoses.sort((a: Pose, b: Pose) => {
            const poseIdA = a.poseId.toUpperCase();
            const poseIdB = b.poseId.toUpperCase();
            
            // Extract prefix and number from poseId (e.g., "F1" -> ["F", "1"], "PF10" -> ["PF", "10"])
            const extractParts = (poseId: string) => {
              const match = poseId.match(/^([A-Z]+)(\d+)$/);
              if (match) {
                return [match[1], parseInt(match[2], 10)];
              }
              return [poseId, 0];
            };
            
            const partsA = extractParts(poseIdA);
            const partsB = extractParts(poseIdB);
            
            // First sort by prefix (F, M, PF)
            const prefixOrder: { [key: string]: number } = { 'F': 1, 'M': 2, 'PF': 3 };
            const prefixA = prefixOrder[partsA[0] as string] || 999;
            const prefixB = prefixOrder[partsB[0] as string] || 999;
            
            if (prefixA !== prefixB) {
              return prefixA - prefixB;
            }
            
            // If same prefix, sort by number
            const numA = partsA[1] as number;
            const numB = partsB[1] as number;
            if (numA !== numB) {
              return numA - numB;
            }
            
            // Fallback to string comparison
            return poseIdA.localeCompare(poseIdB);
          });
          setPoses(sortedPoses);
          
          // Set default pose to "Random Pose" if none is selected
          if (!selectedPoseId) {
            setSelectedPoseId('none'); // 'none' represents Random Pose
            if (onPoseSelect) {
              onPoseSelect('', 'none');
            }
          }
        } else {
          setError('Failed to load poses');
        }
      } catch (error) {
        console.error('Error fetching poses:', error);
        setError('Failed to load poses');
      } finally {
        setLoading(false);
      }
    };

    fetchPoses();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePoseSelect = (pose: Pose) => {
    const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
    // Clean up the URL by removing extra quotes if they exist
    const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
    
    setSelectedPoseId(pose.poseId);
    setIsDropdownOpen(false);
    setIsCollapsed(true); // Collapse after selection
    if (onPoseSelect) {
      onPoseSelect(cleanImageUrl, pose.poseId);
    }
  };

  const getPoseDisplayName = (pose: Pose) => {
    if (pose.poseName && pose.poseName.trim()) {
      return pose.poseName;
    }
    return `${pose.type.charAt(0).toUpperCase() + pose.type.slice(1)} Pose`;
  };

  // Filter poses based on gender, type, and size selection
  const allowedTypes = ['standing', 'leaning', 'sitting', 'back'];
  const filteredPoses = poses.filter(pose => {
    const matchesGender = pose.gender.toLowerCase() === genderFilter.toLowerCase();
    const matchesType = pose.type.toLowerCase() === typeFilter.toLowerCase();
    const isAllowedType = allowedTypes.includes(pose.type.toLowerCase());
    
    // Size filter logic based on size field
    let matchesSize = true;
    if (sizeFilter && pose.size) {
      matchesSize = pose.size.toLowerCase() === sizeFilter.toLowerCase();
    }
    
    return matchesGender && matchesType && isAllowedType && matchesSize;
  });

  const selectedPose = poses.find(p => p.poseId === selectedPoseId);

  // Effect to handle gender filter changes from parent
  useEffect(() => {
    // Skip if no pose is selected or if it's random pose
    if (!selectedPoseId || selectedPoseId === 'none') {
      return;
    }
    
    // If current selected pose doesn't match the filter, default to random pose
    const currentPose = poses.find(p => p.poseId === selectedPoseId);
    if (currentPose && currentPose.gender.toLowerCase() !== genderFilter.toLowerCase()) {
      setSelectedPoseId('none'); // Default to Random Pose
      if (onPoseSelect) {
        onPoseSelect('', 'none');
      }
    }
  }, [genderFilter, selectedPoseId, poses, setSelectedPoseId, onPoseSelect]);

  const handleTypeFilterChange = (type: string) => {
    setTypeFilter(type);
    
    // If current selected pose doesn't match the filter, clear selection
    if (selectedPose && selectedPose.type.toLowerCase() !== type.toLowerCase()) {
      setSelectedPoseId(null);
      if (onPoseSelect) {
        onPoseSelect('', '');
      }
    }
  };

  // Fixed pose types for filter
  const poseTypes = ['standing', 'leaning', 'sitting', 'back'];

  if (loading) {
    return (
      <div className="bg-accent-sand rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-text-heading">Poses</h3>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
            <span className="text-text-description">Loading poses...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-accent-sand rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-text-heading">Poses</h3>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-red-500 text-2xl mb-2"></i>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show collapsed view if a pose is selected and component is collapsed (desktop only)
  if (isCollapsed && selectedPoseId) {
    const selectedPose = poses.find(p => p.poseId === selectedPoseId);
    
    return (
      <div className={`bg-accent-sand rounded-lg p-4 relative ${disabled ? 'opacity-50' : ''}`}>
        {/* Desktop collapsed view */}
        <div className="hidden md:block">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-text-heading">Poses</h3>
            <button
              onClick={() => setIsCollapsed(false)}
              className="text-secondary-600 hover:text-secondary-800 text-sm font-medium"
            >
              Change
            </button>
          </div>
          
          <div className="mt-3 p-3 bg-white rounded-lg border border-accent-taupe">
            <div className="flex items-center space-x-3">
              {selectedPoseId === 'none' ? (
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <i className="fas fa-random text-secondary-600"></i>
                </div>
              ) : selectedPose ? (
                <img
                  src={(() => {
                    const imageUrl = Array.isArray(selectedPose.imageUrl) ? selectedPose.imageUrl[0] : selectedPose.imageUrl;
                    return typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                  })()}
                  alt={getPoseDisplayName(selectedPose)}
                  className="w-12 h-12 object-contain rounded-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                  }}
                />
              ) : null}
              
              <div className="flex-1">
                <p className="text-sm font-medium text-text-heading">
                  {selectedPoseId === 'none' 
                    ? 'Random Pose' 
                    : selectedPose 
                      ? getPoseDisplayName(selectedPose)
                      : 'Selected Pose'
                  }
                </p>
                <p className="text-xs text-text-description">
                  {selectedPoseId === 'none' 
                    ? 'System will choose automatically'
                    : selectedPose 
                      ? `${selectedPose.type} - ${selectedPose.gender}`
                      : 'Pose selected'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile view - always show full component */}
        <div className="md:hidden">
          <div className="flex flex-col gap-2 mb-4">
            <h3 className="text-lg font-medium text-text-heading">Poses</h3>
            
            {/* Type Filter Dropdown */}
            <div className="flex flex-col gap-2">
              <CustomSelect
                value={typeFilter}
                onChange={(value) => handleTypeFilterChange(value)}
                options={poseTypes.map((type) => ({
                  value: type,
                  label: type.charAt(0).toUpperCase() + type.slice(1)
                }))}
                className="w-full"
              />
            </div>
          </div>
          
          {/* Mobile custom dropdown */}
          <div className="relative mb-4" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 bg-white text-left flex items-center justify-between"
            >
              <span className="truncate">
                {selectedPose ? getPoseDisplayName(selectedPose) : selectedPoseId === 'none' ? 'Random Pose' : 'Choose a pose...'}
              </span>
              <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'} text-text-description ml-2`}></i>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-accent-taupe rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {/* Random Pose option */}
                <button
                  onClick={() => {
                    setSelectedPoseId('none');
                    setIsDropdownOpen(false);
                    if (onPoseSelect) {
                      onPoseSelect('', 'none');
                    }
                  }}
                  className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe"
                >
                  <div className="w-12 h-12 bg-secondary-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                    <i className="fas fa-random text-secondary-600"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-heading truncate">
                      Random Pose
                    </p>
                    <p className="text-xs text-text-description truncate">
                      System will choose automatically
                    </p>
                  </div>
                  {selectedPoseId === 'none' && (
                    <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                  )}
                </button>
                
                {filteredPoses.map((pose) => {
                  const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                  const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                  
                  return (
                    <button
                      key={pose.poseId}
                      onClick={() => {
                        const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                        const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                        
                        setSelectedPoseId(pose.poseId);
                        setIsDropdownOpen(false);
                        if (onPoseSelect) {
                          onPoseSelect(cleanImageUrl, pose.poseId);
                        }
                      }}
                      className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe last:border-b-0"
                    >
                      <img
                        src={cleanImageUrl}
                        alt={getPoseDisplayName(pose)}
                        className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-heading truncate">
                          {getPoseDisplayName(pose)}
                        </p>
                        <p className="text-xs text-text-description truncate">
                          {pose.type} - {pose.gender}
                        </p>
                      </div>
                      {selectedPoseId === pose.poseId && (
                        <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Mobile pose preview */}
          {selectedPoseId && selectedPoseId !== 'none' && selectedPose && (
            <div className="space-y-3">
              <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
                <img
                  src={(() => {
                    const imageUrl = Array.isArray(selectedPose.imageUrl) ? selectedPose.imageUrl[0] : selectedPose.imageUrl;
                    return typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                  })()}
                  alt={getPoseDisplayName(selectedPose)}
                  className="w-full h-64 object-contain bg-accent-sand"
                  onError={(e) => {
                    console.error('Image failed to load');
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=Pose+Not+Available';
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                  <p className="text-sm font-medium text-white text-center">
                    {getPoseDisplayName(selectedPose)}
                  </p>
                </div>
              </div>
              
              <div className="p-2 bg-secondary-100 rounded-lg">
                <p className="text-sm text-secondary-700">
                  <i className="fas fa-info-circle mr-1"></i>
                  Selected: {getPoseDisplayName(selectedPose)} - {selectedPose.type} ({selectedPose.gender})
                </p>
              </div>
            </div>
          )}

          {/* Random pose selection info */}
          {selectedPoseId === 'none' && (
            <div className="space-y-3">
              <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
                <div className="w-full h-64 bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
                  <div className="text-center">
                    <i className="fas fa-random text-4xl text-secondary-600 mb-4"></i>
                    <p className="text-lg font-medium text-text-heading">Random Pose</p>
                    <p className="text-sm text-text-description">System will choose automatically</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2 bg-secondary-100 rounded-lg">
                <p className="text-sm text-secondary-700">
                  <i className="fas fa-info-circle mr-1"></i>
                  Selected: Random Pose - System will choose automatically
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-accent-sand rounded-lg p-4 relative flex flex-col h-full ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between mb-4 min-w-0 flex-shrink-0">
        <h3 className="text-lg font-medium text-text-heading flex-shrink-0">Poses</h3>
        
        {/* Type Filter Dropdown */}
        <div className="flex space-x-2 min-w-0 flex-1 justify-end">
          <CustomSelect
            value={typeFilter}
            onChange={(value) => handleTypeFilterChange(value)}
            options={poseTypes.map((type) => ({
              value: type,
              label: type.charAt(0).toUpperCase() + type.slice(1)
            }))}
            className="w-auto min-w-[100px] max-w-[140px]"
          />
        </div>
      </div>
      
      {disabled && (
        <div className="absolute inset-0 bg-accent-sand bg-opacity-90 rounded-lg flex items-center justify-center z-10">
          <div className="text-center p-4">
            <i className="fas fa-lock text-2xl text-text-description mb-2"></i>
            <p className="text-sm font-medium text-text-heading mb-1">Pose Selection Disabled</p>
            <p className="text-xs text-text-description">Feature temporarily unavailable</p>
          </div>
        </div>
      )}
      
      {/* Mobile custom dropdown - shown only on screens smaller than md */}
      <div className="md:hidden">
        <div className="relative mb-4" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 bg-white text-left flex items-center justify-between"
          >
            <span className="truncate">
              {selectedPose ? getPoseDisplayName(selectedPose) : selectedPoseId === 'none' ? 'Random Pose' : 'Choose a pose...'}
            </span>
            <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'} text-text-description ml-2`}></i>
          </button>
          
          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-accent-taupe rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {/* Random Pose option */}
              <button
                onClick={() => {
                  setSelectedPoseId('none');
                  setIsDropdownOpen(false);
                  setIsCollapsed(true); // Collapse after selection
                  if (onPoseSelect) {
                    onPoseSelect('', 'none');
                  }
                }}
                className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe"
              >
                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                  <i className="fas fa-random text-secondary-600"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-heading truncate">
                    Random Pose
                  </p>
                  <p className="text-xs text-text-description truncate">
                    System will choose automatically
                  </p>
                </div>
                {selectedPoseId === 'none' && (
                  <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                )}
              </button>
              
              {filteredPoses.map((pose) => {
                const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                
                return (
                  <button
                    key={pose.poseId}
                    onClick={() => handlePoseSelect(pose)}
                    className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe last:border-b-0"
                  >
                    <img
                      src={cleanImageUrl}
                      alt={getPoseDisplayName(pose)}
                      className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-heading truncate">
                        {getPoseDisplayName(pose)}
                      </p>
                      <p className="text-xs text-text-description truncate">
                        {pose.type} - {pose.gender}
                      </p>
                    </div>
                    {selectedPoseId === pose.poseId && (
                      <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Mobile pose preview */}
        {selectedPoseId && selectedPoseId !== 'none' && selectedPose && (
          <div className="space-y-3">
            <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
              <img
                src={(() => {
                  const imageUrl = Array.isArray(selectedPose.imageUrl) ? selectedPose.imageUrl[0] : selectedPose.imageUrl;
                  return typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                })()}
                alt={getPoseDisplayName(selectedPose)}
                className="w-full h-64 object-contain bg-accent-sand"
                onError={(e) => {
                  console.error('Image failed to load');
                  (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=Pose+Not+Available';
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3">
                <p className="text-sm font-medium text-white text-center">
                  {getPoseDisplayName(selectedPose)}
                </p>
              </div>
            </div>
            
            <div className="p-2 bg-secondary-100 rounded-lg">
              <p className="text-sm text-secondary-700">
                <i className="fas fa-info-circle mr-1"></i>
                Selected: {getPoseDisplayName(selectedPose)} - {selectedPose.type} ({selectedPose.gender})
              </p>
            </div>
          </div>
        )}

        {/* Random pose selection info */}
        {selectedPoseId === 'none' && (
          <div className="space-y-3">
            <div className="relative bg-white rounded-lg overflow-hidden shadow-sm">
              <div className="w-full h-64 bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
                <div className="text-center">
                  <i className="fas fa-random text-4xl text-secondary-600 mb-4"></i>
                  <p className="text-lg font-medium text-text-heading">Random Pose</p>
                  <p className="text-sm text-text-description">System will choose automatically</p>
                </div>
              </div>
            </div>
            
            <div className="p-2 bg-secondary-100 rounded-lg">
              <p className="text-sm text-secondary-700">
                <i className="fas fa-info-circle mr-1"></i>
                Selected: Random Pose - System will choose automatically
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Desktop grid - shown only on md screens and larger */}
      <div className="hidden md:flex flex-1 min-h-0 flex-col">
        <div className="flex-1 overflow-y-auto pr-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 pl-2 pb-2">
          {/* Random Pose card */}
          <button
            onClick={() => {
              setSelectedPoseId('none');
              setIsCollapsed(true); // Collapse after selection
              if (onPoseSelect) {
                onPoseSelect('', 'none');
              }
            }}
            className={`group relative rounded-lg overflow-hidden transition-all duration-200 ${
              selectedPoseId === 'none'
                ? 'ring-4 ring-secondary-500 ring-opacity-50 shadow-lg' 
                : 'hover:shadow-md hover:scale-105'
            }`}
          >
            <div className="w-full h-32 lg:h-28 xl:h-40 bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
              <div className="text-center">
                <i className="fas fa-random text-xl lg:text-lg xl:text-2xl text-secondary-600 mb-2"></i>
                <p className="text-xs lg:text-xs xl:text-sm font-medium text-text-heading">Random</p>
              </div>
            </div>
            <div className={`absolute inset-0 transition-opacity ${
              selectedPoseId === 'none'
                ? 'bg-secondary-500 bg-opacity-20' 
                : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
            }`} />
            {selectedPoseId === 'none' && (
              <div className="absolute top-2 right-2">
                <div className="bg-secondary-500 text-white rounded-full p-1">
                  <i className="fas fa-check text-xs"></i>
                </div>
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 lg:p-1.5 xl:p-3">
              <p className="text-xs lg:text-xs xl:text-sm font-medium text-white text-center leading-tight">
                Random Pose
              </p>
            </div>
          </button>
          
          {filteredPoses.map((pose) => {
            const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
            const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
            const isSelected = selectedPoseId === pose.poseId;
            
            return (
              <button
                key={pose.poseId}
                onClick={() => handlePoseSelect(pose)}
                className={`group relative rounded-lg overflow-hidden transition-all duration-200 ${
                  isSelected 
                    ? 'ring-4 ring-secondary-500 ring-opacity-50 shadow-lg' 
                    : 'hover:shadow-md hover:scale-105'
                }`}
              >
                <img
                  src={cleanImageUrl}
                  alt={getPoseDisplayName(pose)}
                  className="w-full h-32 lg:h-28 xl:h-40 object-contain"
                  onError={(e) => {
                    console.error('Image failed to load:', cleanImageUrl);
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150x200?text=Pose+Not+Available';
                  }}
                />
                <div className={`absolute inset-0 transition-opacity ${
                  isSelected 
                    ? 'bg-secondary-500 bg-opacity-20' 
                    : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
                }`} />
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-secondary-500 text-white rounded-full p-1">
                      <i className="fas fa-check text-xs"></i>
                    </div>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 lg:p-1.5 xl:p-3">
                  <p className="text-xs lg:text-xs xl:text-sm font-medium text-white text-center leading-tight">
                    {getPoseDisplayName(pose)}
                  </p>
                </div>
              </button>
            );
          })}
          </div>
        </div>
        
        {/* Desktop selection info */}
        {selectedPoseId && (
          <div className="mt-3 p-2 bg-secondary-100 rounded-lg flex-shrink-0">
            <p className="text-sm text-secondary-700">
              <i className="fas fa-info-circle mr-1"></i>
              {selectedPoseId === 'none' 
                ? 'Pose selected: Random Pose - System will choose automatically'
                : selectedPose 
                  ? `Pose selected: ${getPoseDisplayName(selectedPose)} - ${selectedPose.type} (${selectedPose.gender})`
                  : 'Pose selected'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PoseSelection;
